import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./PlayerHUD.css";
import { getAvatarImage, shouldMirrorAvatar } from "./avatars";
import { getDamageFlashStyles } from "./effects";
import { getTitleColorHex } from "./constants/titleColors";
import { getTargetingBorderClassName, getTargetingBorderStyles } from "./targeting/TargetingBorder";
import { BattlePlayerRankCorner } from "./pvp/battlePlayerRankCorner";
import { getFactionBoxBackground, getFactionIconPath } from "./utils/factionHudDisplay";
import {
  BATTLE_HEALTH_BARRIER_THRESHOLDS,
  BARRIER_SPRITE_DISMANTLE_DELAY_MS,
  BATTLE_HEALTH_MAX_VISUAL,
} from "./constants/battleHealthHud";
import { useDelayedBarrierSpriteDisplay } from "./hooks/useDelayedBarrierSpriteDisplay";

/**
 * PlayerHUD
 *
 * Player heads-up display rendered in the bottom-left corner of the battlefield.
 * Uses /images/hud3.png (PNG with transparent slot interiors) as the frame.
 *
 * The colored fills (bars, orbs, faction color, avatar) are layered BEHIND
 * the frame. The frame's transparent cutouts naturally mask each fill to its
 * exact slot shape (angular bar tips, faction pentagon, etc.) — no clip-paths
 * needed.
 *
 * Overlays:
 *   - the player's General avatar inside the gold circle (with damage flash,
 *     mirror, targeting border, click handler for emotes)
 *   - the player's battle title in the upper title box
 *   - the player's faction color in the small pentagon attached below the circle
 *   - the player's current health in the upper bar (with hex barriers + active
 *     turn glow + numeric label)
 *   - the player's current intel in the lower bar (with numeric label)
 *   - turn arrows (your turn) or pause chip (waiting) beside the title box
 *   - a heart icon in the small orb on the left of the health bar
 *   - a brain icon in the small orb on the left of the intel bar
 *
 * Resize the entire HUD coherently via the --hud-scale variable in
 * PlayerHUD.css. Overlay coordinates are NATIVE pixels relative to the
 * cropped frame region (see PlayerHUD.css for the source slot bounding boxes).
 */
const PlayerHUD = ({
  health,
  intelligenceLeft,
  intelligenceTotal,
  barriersRemoved = [],
  damageFlash = false,
  factionName = null,
  translatedFactionName = null,
  isActiveTurn = false,
  selectedGeneral,
  avatars,
  onAvatarClick,
  isGeneralTarget = false,
  yourTurn,
  playerTitle,
  playerTitleColor,
  playerRank,
  playerPvpLadderRow,
  getRankStyle,
  notYourTurnLabel = "",
  avatarRef,
  ordersReady = 0,
  strategySize = 0,
  completedOrders = 0,
  onOpenStrategy,
  onOpenCompletedOrders,
  ordersReadyLabel = "Orders ready",
  strategySizeLabel = "Strategy size",
  completedOrdersLabel = "Completed orders",
  clickToViewStrategyLabel = "Click to view strategy",
  clickToViewCompletedOrdersLabel = "Click to view completed orders",
  isCommandDirectivePending = false,
  preparationBonus = null,
  preparationBonusUsed = false,
  preparationBonusPending = false,
  yourTurnForPrepBonus = false,
  canShowPreparationBonus = false,
  onActivatePreparationBonus,
  preparationBonusTooltip = "",
  diplomaticAccord = null,
  diplomaticAccordTooltip = "",
  barrierSpriteDelayMs = BARRIER_SPRITE_DISMANTLE_DELAY_MS,
  barrierVisualResetKey = null,
}) => {
  const { isBarrierSpriteDismantled } = useDelayedBarrierSpriteDisplay(
    health,
    barriersRemoved,
    barrierSpriteDelayMs,
    barrierVisualResetKey
  );
  const healthPercent = Math.min(100, Math.max(0, (health / BATTLE_HEALTH_MAX_VISUAL) * 100));
  const intelTotal = Math.max(1, intelligenceTotal || 0);
  const intelligencePercent = Math.max(0, Math.min(100, ((intelligenceLeft || 0) / intelTotal) * 100));
  const titleRef = useRef(null);
  const [hudTooltip, setHudTooltip] = useState(null); // { text, x, y }

  const generalTargetingBorder = isGeneralTarget ? getTargetingBorderStyles(true) : null;

  const hudMaskUrl = "url(/images/hud3_mask.png)";
  const preparationBonusImage = preparationBonus === "plan_ahead"
    ? "plan_ahead.jpg"
    : preparationBonus === "black_market"
      ? "black_market_dossier.jpg"
      : preparationBonus === "team_leader"
        ? "team_leader_briefing.jpg"
        : null;
  const diplomaticAccordImage = diplomaticAccord === "strategic_coordination"
    ? "strategic_coordination.jpg"
    : diplomaticAccord === "public_relations"
      ? "public_relations.jpg"
      : diplomaticAccord === "emergency_protocol"
        ? "emergency_protocol.jpg"
        : diplomaticAccord === "command_directive"
          ? "command_directive.jpg"
          : null;
  const prepDisabled = preparationBonusUsed || preparationBonusPending || !yourTurnForPrepBonus;
  const factionIconPath = getFactionIconPath(factionName);

  const fitTitleText = () => {
    const el = titleRef.current;
    if (!el) return;

    const minSize = 8;
    const step = 0.5;
    const MAX_TITLE_FONT_PX = 15;
    const maxSize = Math.min(MAX_TITLE_FONT_PX, Math.max(12, Math.floor(el.clientHeight * 0.9)));

    el.style.fontSize = `${maxSize}px`;
    el.style.lineHeight = "1";
    if (!playerTitle) return;

    let size = maxSize;
    while (
      size > minSize &&
      (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)
    ) {
      size -= step;
      el.style.fontSize = `${size}px`;
    }
  };

  const showHudTooltip = useCallback((event, text) => {
    if (!text) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setHudTooltip({
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
  }, []);

  const hideHudTooltip = useCallback(() => {
    setHudTooltip(null);
  }, []);

  useLayoutEffect(() => {
    fitTitleText();
  }, [playerTitle]);

  useEffect(() => {
    const handleResize = () => fitTitleText();
    window.addEventListener("resize", handleResize);

    // Re-fit after async webfont loading (e.g. Orbitron).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        fitTitleText();
      });
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      className={`player-hud-container ${isActiveTurn ? "player-hud-active-turn" : ""}`}
      style={{ "--hud-mask": hudMaskUrl }}
    >
      <div className="player-hud-scaler">
        {/* === LAYER 1: fills behind the frame === */}

        {/* Avatar in gold circle */}
        <div className="player-hud-circle">
          <div
            className={`player-hud-circle-target ${getTargetingBorderClassName(isGeneralTarget) || ""}`}
          >
            <img
              ref={avatarRef}
              src={getAvatarImage(selectedGeneral)}
              alt="Player General"
              data-zone="player_general"
              onClick={onAvatarClick}
              className="player-hud-avatar"
              style={{
                transform: shouldMirrorAvatar(selectedGeneral, "left", avatars) ? "scaleX(-1)" : "none",
                ...getDamageFlashStyles(damageFlash),
                border: generalTargetingBorder ? generalTargetingBorder.border : "none",
                cursor: onAvatarClick ? "pointer" : "default",
              }}
            />
          </div>
        </div>

        {/* Player banner protruding from avatar circle */}
        <div className="player-hud-player-banner" aria-hidden="true">
          <img
            src="/images/banner_player.png"
            alt=""
            className="player-hud-player-banner-image"
            draggable={false}
          />
        </div>

        {/* Rank insignia rendered above the banner image (same anchor area). */}
        <div className="player-hud-banner-rank-anchor">
          <BattlePlayerRankCorner
            playerRank={playerRank}
            playerPvpLadderRow={playerPvpLadderRow}
            getRankStyle={getRankStyle}
          />
        </div>

        {/* Health bar (fill + shield barriers); data-tutorial-hud: campaign tutorial pulse (chapter_1_mission_1) */}
        <div className="player-hud-bar player-hud-health" data-tutorial-hud="health-bar">
          <div className="player-hud-health-fill" style={{ width: `${healthPercent}%` }} />
        </div>

        {/* Shield barriers rendered ABOVE the bar/frame so they are not clipped */}
        {BATTLE_HEALTH_BARRIER_THRESHOLDS.map((threshold) => {
          const positionPx = 168 + (threshold / BATTLE_HEALTH_MAX_VISUAL) * 204;
          const destroyed = isBarrierSpriteDismantled(threshold);
          const src = `/images/${destroyed ? "barrier_destroyed.png" : "barrier_healthy.png"}`;
          return (
            <img
              key={threshold}
              src={src}
              alt=""
              aria-hidden="true"
              data-tutorial-hud="barrier"
              className={`player-hud-barrier ${destroyed ? "player-hud-barrier-destroyed" : "player-hud-barrier-healthy"}`}
              style={{ left: `${positionPx}px` }}
              data-tooltip={`Health threshold: ${threshold}`}
              draggable={false}
            />
          );
        })}

        {/* Health orb (red gradient with current health number) */}
        <div className="player-hud-orb player-hud-health-orb" data-tutorial-hud="health-orb" aria-label={`Health: ${health}`}>
          <span className="player-hud-orb-text">{health}</span>
        </div>

        {/* Intel bar (fill) */}
        <div className="player-hud-bar player-hud-intel" data-tutorial-hud="intel-bar">
          <div className="player-hud-intel-fill" style={{ width: `${intelligencePercent}%` }} />
        </div>

        {/* Intel orb (blue gradient with current/max intel) */}
        <div
          className="player-hud-orb player-hud-intel-orb"
          data-tutorial-hud="intel-orb"
          aria-label={`Intelligence: ${intelligenceLeft}/${intelligenceTotal}`}
        >
          <span className="player-hud-orb-text player-hud-orb-text-intel">
            {intelligenceLeft}/{intelligenceTotal}
          </span>
        </div>

        {/* O/S/C flags attached to intel bar (protruding like mini banners). */}
        <div className="player-hud-intel-flags">
          <div
            className="player-hud-intel-flag player-hud-intel-flag-static"
            onMouseEnter={(e) => showHudTooltip(e, ordersReadyLabel)}
            onMouseLeave={hideHudTooltip}
          >
            <span className="player-hud-intel-flag-label">O:</span>
            <span className="player-hud-intel-flag-value">{ordersReady}</span>
          </div>
          <button
            className={`player-hud-intel-flag player-hud-intel-flag-button ${isCommandDirectivePending ? "player-hud-intel-flag-command-directive" : ""}`}
            onClick={onOpenStrategy}
            type="button"
            onMouseEnter={(e) => showHudTooltip(e, clickToViewStrategyLabel)}
            onMouseLeave={hideHudTooltip}
          >
            <span className="player-hud-intel-flag-label">
              S:
            </span>
            <span className="player-hud-intel-flag-value">{strategySize}</span>
          </button>
          <button
            className="player-hud-intel-flag player-hud-intel-flag-button"
            onClick={onOpenCompletedOrders}
            type="button"
            onMouseEnter={(e) => showHudTooltip(e, clickToViewCompletedOrdersLabel)}
            onMouseLeave={hideHudTooltip}
          >
            <span className="player-hud-intel-flag-label">
              C:
            </span>
            <span className="player-hud-intel-flag-value">{completedOrders}</span>
          </button>
        </div>

        {/* Player profile utility buttons (prep bonus + diplomatic accord) below O/S/C flags */}
        <div className="player-hud-profile-buttons">
          {canShowPreparationBonus && preparationBonusImage && (
            <button
              type="button"
              className={`player-hud-profile-button ${prepDisabled ? "player-hud-profile-button-disabled" : ""}`}
              onClick={(e) => {
                if (prepDisabled || !onActivatePreparationBonus) return;
                onActivatePreparationBonus(e);
              }}
              disabled={prepDisabled}
              onMouseEnter={(e) => showHudTooltip(e, preparationBonusTooltip)}
              onMouseLeave={hideHudTooltip}
            >
              <img
                src={`/images/${preparationBonusImage}`}
                alt={preparationBonus || "preparation bonus"}
                draggable={false}
              />
            </button>
          )}
          {diplomaticAccordImage && (
            <div
              className="player-hud-profile-button player-hud-profile-button-static"
              onMouseEnter={(e) => showHudTooltip(e, diplomaticAccordTooltip)}
              onMouseLeave={hideHudTooltip}
            >
              <img
                src={`/images/${diplomaticAccordImage}`}
                alt={diplomaticAccord || "diplomatic accord"}
                draggable={false}
              />
            </div>
          )}
        </div>

        {/* Faction color: hit area has no CSS mask so [data-tooltip]::after from BattleUI.css is not clipped */}
        <div
          className="player-hud-faction-hit"
          data-tooltip={translatedFactionName || factionName || undefined}
          data-tooltip-position="bottom"
        >
          <div
            className="player-hud-faction"
            style={{ background: getFactionBoxBackground(factionName) }}
          >
            {factionIconPath && (
              <img
                src={factionIconPath}
                alt=""
                className="player-hud-faction-icon"
                draggable={false}
                aria-hidden="true"
              />
            )}
          </div>
        </div>

        {/* Dark-blue fill behind the title hex (clipped to slot shape by mask) */}
        <div className="player-hud-title-bg" aria-hidden="true" />

        {/* === LAYER 2: the frame itself === */}
        <div
          className="player-hud-frame"
          aria-hidden="true"
          style={{ backgroundImage: "url(/images/hud3.png)" }}
        />

        {/* === LAYER 3: text labels and decorative items above the frame === */}

        {/* Title */}
        <div
          ref={titleRef}
          className="player-hud-title"
          data-tooltip={playerTitle || undefined}
          style={{ color: getTitleColorHex(playerTitleColor) }}
        >
          {playerTitle || ""}
        </div>

        {/* Turn indicator anchored beside the title box */}
        {yourTurn ? (
          <div className="player-hud-turn-arrows" aria-hidden="true">
            <span className="player-hud-turn-arrow" style={{ animationDelay: "0s" }} />
            <span className="player-hud-turn-arrow" style={{ animationDelay: "0.2s" }} />
            <span className="player-hud-turn-arrow" style={{ animationDelay: "0.4s" }} />
          </div>
        ) : (
          <div
            className="player-hud-paused"
            aria-hidden="true"
            data-tooltip={notYourTurnLabel || undefined}
          >
            <span className="player-hud-paused-bar" />
            <span className="player-hud-paused-bar" />
          </div>
        )}

      </div>
      {hudTooltip && typeof document !== "undefined" && createPortal(
        <div
          style={{
            position: "fixed",
            left: `${hudTooltip.x}px`,
            top: `${hudTooltip.y}px`,
            transform: "translate(-50%, -100%)",
            background: "linear-gradient(135deg, #1a1a2e, #16213e)",
            color: "#ffffff",
            border: "2px solid #00bcd4",
            borderRadius: "8px",
            padding: "8px 12px",
            fontSize: "13px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 10000001,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 188, 212, 0.3)",
            fontFamily: "'Exo 2', sans-serif",
            letterSpacing: "0.5px",
          }}
          aria-hidden="true"
        >
          {hudTooltip.text}
        </div>,
        document.body
      )}
    </div>
  );
};

export default PlayerHUD;
